/**
 * LightCollector — portable light collection from Level → LightManager.
 *
 * Iterates all GameObjects in the given Level, reads PointLight2D,
 * SpotLight2D, DirectionalLight2D, and AmbientLight2D components,
 * converts them to render2d light descriptors and feeds them into
 * the provided LightManager.
 *
 * Zero editor dependencies — works identically in editor and player.
 *
 * @module @engine/core
 */

import type { Level } from './Level';
import type {
  LightManager,
  Camera2D,
  EmissionMode,
} from '@engine/render2d';
import {
  createPointLight,
  createSpotLight,
  createDirectionalLight,
  createAmbientLight,
} from '@engine/render2d';

// ============================================================================
// HELPERS
// ============================================================================

/** ECS component colour {r,g,b} 0-255 → render2d [r,g,b] 0-1. */
function colorToLinear(c: { r: number; g: number; b: number }): [number, number, number] {
  return [c.r / 255, c.g / 255, c.b / 255];
}

/** Degrees → radians. */
function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// ============================================================================
// PUBLIC API
// ============================================================================

export interface LightCollectStats {
  point: number;
  spot: number;
  directional: number;
  hasAmbient: boolean;
}

/**
 * Collect all lights from `level` and upload them into `lightManager`.
 *
 * Call once per frame, between a camera-aware render pass begin/end.
 *
 * @param level          - The Level to iterate
 * @param lightManager   - LightManager instance (from @engine/render2d)
 * @param camera         - Active Camera2D for frustum culling inside LightManager
 * @param viewportWidth  - Viewport width in pixels
 * @param viewportHeight - Viewport height in pixels
 * @returns Light counts for stats / debugging
 */
export function collectLightsFromLevel(
  level: Level,
  lightManager: LightManager,
  camera: Camera2D,
  viewportWidth: number,
  viewportHeight: number,
): LightCollectStats {
  lightManager.begin(camera);

  let point = 0;
  let spot = 0;
  let directional = 0;
  let hasAmbient = false;

  for (const go of level.getAllObjects()) {
    // ── Point Light ──
    const pointData = go.getComponentData('core.PointLight2D');
    if (pointData && pointData.enabled) {
      const t = go.transform;
      // IES rotation = transform rotation + user offset
      const iesRot = (t.rotation ?? 0) + (pointData.iesRotation ?? 0);
      lightManager.addLight(
        createPointLight({
          position: [t.position.x, t.position.y],
          color: colorToLinear(pointData.color),
          emissionMode: (pointData.emissionMode as string as EmissionMode) ?? 'color',
          temperature: pointData.temperature ?? 6500,
          tint: pointData.tint ?? 0,
          intensity: pointData.intensity,
          radius: pointData.radius,
          falloff: pointData.falloff,
          enabled: true,
          castShadows: pointData.castShadows ?? false,
          shadowSoftness: pointData.shadowSoftness ?? 0.5,
          shadowBias: pointData.shadowBias ?? 0.01,
          iesProfile: pointData.iesProfile ?? '',
          iesIntensity: pointData.iesIntensity ?? 1.0,
          iesRotation: iesRot,
          volumetricIntensity: pointData.volumetricIntensity ?? 0,
          volumetricScattering: pointData.volumetricScattering ?? 0.1,
        }),
      );
      point++;
    }

    // ── Spot Light ──
    const spotData = go.getComponentData('core.SpotLight2D');
    if (spotData && spotData.enabled) {
      const t = go.transform;
      // Spotlight IES: direction angle computed in shader, iesRotation is offset only
      lightManager.addLight(
        createSpotLight({
          position: [t.position.x, t.position.y],
          direction: [spotData.direction.x, spotData.direction.y],
          color: colorToLinear(spotData.color),
          emissionMode: (spotData.emissionMode as string as EmissionMode) ?? 'color',
          temperature: spotData.temperature ?? 6500,
          tint: spotData.tint ?? 0,
          intensity: spotData.intensity,
          radius: spotData.radius,
          falloff: spotData.falloff,
          innerAngle: degToRad(spotData.innerAngleDeg),
          outerAngle: degToRad(spotData.outerAngleDeg),
          enabled: true,
          castShadows: spotData.castShadows ?? false,
          shadowSoftness: spotData.shadowSoftness ?? 0.5,
          shadowBias: spotData.shadowBias ?? 0.01,
          iesProfile: spotData.iesProfile ?? '',
          iesIntensity: spotData.iesIntensity ?? 1.0,
          iesRotation: spotData.iesRotation ?? 0,
          volumetricIntensity: spotData.volumetricIntensity ?? 0,
          volumetricScattering: spotData.volumetricScattering ?? 0.1,
        }),
      );
      spot++;
    }

    // ── Directional Light ──
    const dirData = go.getComponentData('core.DirectionalLight2D');
    if (dirData && dirData.enabled) {
      lightManager.addLight(
        createDirectionalLight({
          direction: [dirData.direction.x, dirData.direction.y],
          color: colorToLinear(dirData.color),
          emissionMode: (dirData.emissionMode as string as EmissionMode) ?? 'color',
          temperature: dirData.temperature ?? 6500,
          tint: dirData.tint ?? 0,
          intensity: dirData.intensity,
          enabled: true,
        }),
      );
      directional++;
    }

    // ── Ambient Light ──
    const ambientData = go.getComponentData('core.AmbientLight2D');
    if (ambientData) {
      lightManager.setAmbient(
        createAmbientLight({
          color: colorToLinear(ambientData.color),
          emissionMode: (ambientData.emissionMode as string as EmissionMode) ?? 'color',
          temperature: ambientData.temperature ?? 6500,
          tint: ambientData.tint ?? 0,
          intensity: ambientData.intensity,
        }),
      );
      hasAmbient = true;
    }
  }

  lightManager.end(viewportWidth, viewportHeight);

  return { point, spot, directional, hasAmbient };
}
